import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  forwardRef,
  OnInit,
  OnChanges,
  OnDestroy,
  ChangeDetectorRef,
  ViewChild,
  ElementRef,
  inject
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  AbstractControl,
  FormControl,
  ValidatorFn,
  ValidationErrors
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { UseHttpImageSourcePipe } from '../../pipes';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import lottie, { AnimationItem } from 'lottie-web';

export interface FileUploadConfig {
  label?: string;
  placeholder?: string;
  required?: boolean;
  allowedTypes?: string[];
  maxSizeInBytes?: number;
  showPreview?: boolean;
  acceptAttribute?: string;
  isRtl?: boolean;
  isExternalDragAndDrop?: boolean;
  enableImageCropping?: boolean; // Enable image cropping dialog
  showFileTypeHint?: boolean; // Show file type hint below upload
  fileTypeHintText?: string; // Custom file type hint text
  skipDeleteConfirmation?: boolean; // Skip confirmation dialog when deleting files
  showOcrLoader?: boolean; // Show OCR loader animation
  imageUrlParams?: {
    // Server image URL parameters
    userId?: string;
    isEvent?: boolean;
    isPhysician?: boolean;
    isProfile?: boolean;
    [key: string]: any;
  };
  validationMessages?: {
    required?: string;
    invalidType?: string;
    maxSizeExceeded?: string;
  };
}

export interface FileUploadValue {
  file: File | null;
  imageUrl: string;
  hasExisting: boolean;
}

@Component({
  selector: 'educare-file-upload',
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.scss'],
  imports: [CommonModule, TranslateModule, UseHttpImageSourcePipe, MatCardModule, MatButtonModule, MatIconModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FileUploadComponent),
      multi: true
    }
  ]
})
export class FileUploadComponent implements ControlValueAccessor, OnInit, OnChanges, OnDestroy {
  private readonly dialog = inject(MatDialog);
  private readonly cdr = inject(ChangeDetectorRef);

  @ViewChild('ocrLoaderContainer', { static: false }) ocrLoaderContainer!: ElementRef;
  private ocrAnimation!: AnimationItem;

  private _config!: FileUploadConfig;
  @Input()
  set config(value: FileUploadConfig) {
    this._config = value;
  }
  get config(): FileUploadConfig {
    return this._config;
  }
  @Input() existingImageUrl: string = ''; // Keep this for existing images
  @Input() formControl: AbstractControl | FormControl | null = null;

  @Output() fileSelected = new EventEmitter<File | null>();
  @Output() fileRemoved = new EventEmitter<void>();
  @Output() imageClicked = new EventEmitter<{ url: string; isNewFile: boolean }>();
  @Output() imageCropRequested = new EventEmitter<any>(); // Emit when image cropping is needed
  @Output() pdfPreview = new EventEmitter<File | string>();
  selectedFile: File | null = null;
  imageUrl: string = '';
  hasExistingImage: boolean = false;
  isDisabled: boolean = false;
  isDragOver: boolean = false;
  private uniqueId: string = ''; // Make it private since it's internal

  // Default configuration
  private defaultConfig: FileUploadConfig = {
    label: 'Image Upload',
    placeholder: 'Upload Image',
    required: false,
    allowedTypes: ['image/jpeg', 'image/jpg', 'image/png'],
    maxSizeInBytes: 5 * 1024 * 1024, // 5MB
    showPreview: true,
    acceptAttribute: 'image/*',
    validationMessages: {
      required: 'Image is required',
      invalidType: 'Invalid file type. Please upload JPEG, JPG, or PNG files only',
      maxSizeExceeded: 'File size exceeds 5MB limit'
    }
  };

  // ControlValueAccessor implementation
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private onChange = (_value: any) => {};
  private onTouched = () => {};

  ngOnInit(): void {
    // Don't merge configs in a way that breaks the reference
    // Instead, use getters to access merged values
    this.uniqueId = this.generateUniqueId();
    this.hasExistingImage = !!this.existingImageUrl;
    if (this.hasExistingImage) {
      this.imageUrl = this.existingImageUrl;
    }
  }

  ngOnChanges(): void {
    // Handle OCR loader visibility changes
    if (this.config?.showOcrLoader) {
      this.playOcrAnimation();
    } else {
      this.stopOcrAnimation();
    }
  }

  ngOnDestroy(): void {
    // Clean up animation when component is destroyed
    this.stopOcrAnimation();
  }

  // ControlValueAccessor methods
  writeValue(value: any): void {
    if (value) {
      if (value instanceof File) {
        this.selectedFile = value;
        this.readFileAsDataURL(value);
      } else if (typeof value === 'string') {
        this.imageUrl = value;
        this.hasExistingImage = true;
      } else if (value && typeof value === 'object') {
        this.selectedFile = value.file || null;
        this.imageUrl = value.imageUrl || '';
        this.hasExistingImage = value.hasExisting || false;
      }
    } else {
      this.selectedFile = null;
      this.imageUrl = '';
      this.hasExistingImage = false;
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    // Store disabled state for future use if needed
    this.isDisabled = isDisabled;
  }

  onFileSelected(event: any): void {
    const file = event?.target?.files[0];
    if (!file) return;

    this.onTouched();

    if (this.formControl) {
      this.formControl.markAsTouched();
      this.formControl.markAsDirty();
    }

    // Validate file type
    if (!this.allowedTypes.includes(file.type)) {
      // Set a special error object as the form value so the validator can detect it
      if (this.formControl) {
        this.formControl.markAsTouched();
        this.formControl.markAsDirty();
        // Set a special error object that the parent component can recognize
        this.formControl.setValue({ __error: 'invalidType', __originalFile: file });
        this.formControl.updateValueAndValidity();
        this.cdr.detectChanges();
      }
      return;
    }

    // Validate file size
    if (file.size > this.maxSizeInBytes) {
      // Set a special error object as the form value so the validator can detect it
      if (this.formControl) {
        this.formControl.markAsTouched();
        this.formControl.markAsDirty();
        // Set a special error object that the parent component can recognize
        this.formControl.setValue({ __error: 'maxSizeExceeded', __originalFile: file });
        this.formControl.updateValueAndValidity();
        this.cdr.detectChanges();
      }
      return;
    }

    // File is valid - check if image cropping is enabled
    if (this.config.enableImageCropping && this.isImageFile(file)) {
      // Emit event for parent to handle image cropping
      this.imageCropRequested.emit(event);
      return;
    }

    // Process file normally
    this.processValidFile(file);

    // Clear the input to allow selecting the same file again
    event.target.value = '';
  }

  /**
   * Process a valid file (called after validation and optional cropping)
   */
  processValidFile(file: File): void {
    this.selectedFile = file;
    this.hasExistingImage = false;
    this.readFileAsDataURL(file);
    this.clearValidationErrors();

    // Emit the file and update form control
    this.fileSelected.emit(file);
    this.onChange(file);
  }

  /**
   * Set the cropped file (called by parent after cropping)
   */
  setCroppedFile(file: File): void {
    if (file) {
      this.processValidFile(file);
    }
  }

  /**
   * Check if file is an image
   */
  private isImageFile(file: File): boolean {
    return file.type.startsWith('image/');
  }

  // Drag and Drop Event Handlers
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (!this.isDisabled) {
      this.isDragOver = true;
    }
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    // Only set isDragOver to false if we're leaving the component entirely
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const x = event.clientX;
    const y = event.clientY;

    if (x < rect.left || x >= rect.right || y < rect.top || y >= rect.bottom) {
      this.isDragOver = false;
    }
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    if (this.isDisabled) {
      return;
    }

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      // Create a fake event object similar to file input change event
      const fakeEvent = {
        target: {
          files: files
        }
      };
      this.onFileSelected(fakeEvent);
    }
  }

  onDragEnter(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  /**
   * Static validator function to handle special error objects from FileUploadComponent
   * Use this validator on form controls that use FileUploadComponent
   */
  static fileValidator(hasExisting: () => boolean = () => false, required: boolean = false): ValidatorFn {
    return (ctrl: AbstractControl): ValidationErrors | null => {
      // Check if it's a special error object from FileUploadComponent
      if (ctrl.value && typeof ctrl.value === 'object' && ctrl.value.__error) {
        const errorType = ctrl.value.__error;
        return { [errorType]: true };
      }

      // Check if it's a valid file
      if (ctrl.value instanceof File) return null;

      // Check if there's an existing file
      if (hasExisting()) return null;

      // Check if required
      if (required && !ctrl.value) {
        return { required: true };
      }

      return null;
    };
  }

  removeSelectedFile(): void {
    // Check if confirmation dialog should be skipped
    if (this.config.skipDeleteConfirmation) {
      this.performFileRemoval();
      return;
    }

    // Show confirmation dialog
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'Are You Sure?',
        description: 'Are you sure you want to remove the uploaded image?',
        submitButtonText: 'Yes',
        cancelButtonText: 'No'
      },
      width: '630px'
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.action) {
        this.performFileRemoval();
      }
    });
  }

  private performFileRemoval(): void {
    this.clearFile();
    this.hasExistingImage = false;
    this.clearValidationErrors();

    if (this.config.required && this.formControl) {
      this.formControl.markAsTouched();
      this.formControl.markAsDirty();
      // Use special error object for consistency with validation system
      this.formControl.setValue({ __error: 'required' });
      this.formControl.updateValueAndValidity();
      this.cdr.detectChanges();
    }

    this.fileRemoved.emit();
    this.onChange(null);
  }

  openImageDialog(imageUrl: string): void {
    // Emit structured data based on whether it's a new file or existing image
    if (this.selectedFile) {
      // It's a new uploaded file
      this.imageClicked.emit({ url: imageUrl, isNewFile: true });
    } else {
      // It's an existing image
      this.imageClicked.emit({ url: this.existingImageUrl, isNewFile: false });
    }
  }

  private clearFile(): void {
    this.selectedFile = null;
    this.imageUrl = '';
  }

  private readFileAsDataURL(file: File): void {
    const reader = new FileReader();
    reader.onload = () => {
      this.imageUrl = reader.result as string;
    };
    reader.readAsDataURL(file);
  }

  private clearValidationErrors(): void {
    if (this.formControl) {
      this.formControl.setErrors(null);
      this.formControl.updateValueAndValidity();
    }
  }

  private generateUniqueId(): string {
    return 'file-upload-' + Math.random().toString(36).substring(2, 11);
  }

  get displayImageUrl(): string {
    if (this.selectedFile) {
      return this.imageUrl;
    }
    return this.existingImageUrl || this.imageUrl;
  }

  get hasFile(): boolean {
    return !!this.selectedFile || !!this.existingImageUrl;
  }

  get fileName(): string {
    return this.selectedFile?.name || '';
  }

  get inputId(): string {
    return `file-upload-${this.uniqueId}`;
  }

  get isRtl(): boolean {
    return !!this.config.isRtl;
  }

  get allowedTypes(): string[] {
    return this.config.allowedTypes || this.defaultConfig.allowedTypes!;
  }

  get maxSizeInBytes(): number {
    return this.config.maxSizeInBytes || this.defaultConfig.maxSizeInBytes!;
  }

  get isPdfFile(): boolean {
    if (this.selectedFile) {
      return this.selectedFile.type === 'application/pdf';
    }
    if (this.existingImageUrl) {
      return this.existingImageUrl.toLowerCase().endsWith('.pdf');
    }
    return false;
  }

  // OCR Animation methods
  private playOcrAnimation(): void {
    if (this.ocrLoaderContainer) {
      this.ocrAnimation = lottie.loadAnimation({
        container: this.ocrLoaderContainer.nativeElement,
        path: '/assets/OCRAnimation.json',
        renderer: 'svg',
        loop: true,
        autoplay: true
      });
    }
  }

  private stopOcrAnimation(): void {
    if (this.ocrAnimation) {
      this.ocrAnimation.destroy();
    }
  }

  onPdfIconClick() {
    this.pdfPreview.emit(this.selectedFile || this.displayImageUrl);
  }
}
